import React, { useState } from 'react';
import { MailIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { TextField } from '../components/forms/TextField';
import { TextAreaField } from '../components/forms/TextAreaField';
import { SelectField } from '../components/forms/SelectField';
import { FormSuccess } from '../components/forms/FormSuccess';
import { Button } from '../components/Button';
import { images } from '../data/images';
import { artists } from '../data/artists';
import { contact } from '../data/site';
type Status = 'idle' | 'submitting' | 'done';
export function BookNow() {
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const next: Record<string, string> = {};
    const phone = String(data.get('phone') ?? '');
    if (phone.replace(/\D/g, '').length < 10) next.phone = 'Please enter a valid phone number.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('done'), 1000);
  };
  return <>
      <PageHero title="Book Now" subtitle="Appointment Request" image={images.heart} data-id="element-286" data-mp="3r7nq4neldk6u">
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-287" data-mp="3n6alggn8jn1i">
          Are you excited to get Tattooed or Pierced? Get yourself a Tattoo or Piercing, by Requesting to Schedule an Appointment.
        </p>
      </PageHero>

      <section className="mx-auto grid max-w-6xl gap-14 px-6 py-20 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-288" data-mp="38ejx94977bda">
        <div data-id="element-289" data-mp="33h0a1euivo97">
          {status === 'done' ? <FormSuccess title="Request sent" onReset={() => setStatus('idle')} resetLabel="Send another request" data-id="element-290" data-mp="33e5pzoff85gb">
              Thanks for reaching out! An artist will get back to you shortly to confirm your appointment. For faster service, text us at {contact.phoneMain}.
            </FormSuccess> : <form onSubmit={handleSubmit} noValidate={false} className="space-y-6" data-id="element-291" data-mp="2otjpux87oxrb">
              <div className="grid gap-6 sm:grid-cols-2" data-id="element-292" data-mp="2or6y5scnj912">
                <TextField label="First name" name="firstName" required autoComplete="given-name" data-id="element-293" data-mp="2av24v79oxzd3" />
                <TextField label="Last name" name="lastName" required autoComplete="family-name" data-id="element-294" data-mp="2ftmu6bnemymr" />
                <TextField label="Email" name="email" type="email" required autoComplete="email" data-id="element-295" data-mp="33jqy6k4i90ea" />
                <TextField label="Phone" name="phone" type="tel" required autoComplete="tel" error={errors.phone} data-id="element-296" data-mp="38g7mbebpferd" />
                <SelectField label="Service" name="service" required options={['Tattoo', 'Piercing', 'Consultation']} data-id="element-297" data-mp="2tscrnse4z0bn" />
                <SelectField label="Preferred artist" name="artist" options={['First available', ...artists.map(a => a.name)]} data-id="element-298" data-mp="2ypcyi5it873t" />
                <TextField label="Preferred date" name="date" type="date" required data-id="element-299" data-mp="3r8ik4tri4mim" />
                <SelectField label="Preferred time" name="time" options={['Afternoon (12pm – 4pm)', 'Evening (4pm – 8pm)', 'Late (8pm – 10pm)']} data-id="element-300" data-mp="1ft1gq4icyp5o" />
                <TextField label="Placement" name="placement" placeholder="e.g. left forearm" data-id="element-301" data-mp="1at5fue4yjduc" />
                <TextField label="Approximate size" name="size" placeholder="e.g. 4 x 6 inches" data-id="element-302" data-mp="1ok1v739vhyjg" />
              </div>
              <TextAreaField label="Describe your idea" name="description" required placeholder="Style, colors, references, anything that helps us understand your vision." data-id="element-303" data-mp="1kkjkt1foh5uv" />
              <TextField label="Reference images" name="references" type="file" accept="image/*" multiple hint="Optional — up to 5 images." className="[&_input]:py-2.5" data-id="element-304" data-mp="15vddeq6os9bw" />
              <div className="flex flex-wrap items-center gap-4 pt-2" data-id="element-305" data-mp="10y3pjeyy45q6">
                <Button type="submit" loading={status === 'submitting'} data-id="element-306" data-mp="2h8619pwcwdmo">
                  Request appointment
                </Button>
                <p className="text-sm text-ink-faint" data-id="element-307" data-mp="37l44q4c4ynsl">A deposit may be required to secure your spot.</p>
              </div>
            </form>}
        </div>

        <aside className="space-y-8 lg:border-l lg:border-ink-line lg:pl-10" data-id="element-308" data-mp="3ckfkpb4ohp94">
          <div data-id="element-309" data-mp="3hih642pku0nr">
            <h2 className="font-slab text-sm font-bold uppercase tracking-[0.2em]" data-id="element-310" data-mp="3hhyyk9xgs00j">Walk-ins welcome</h2>
            <p className="mt-3 leading-relaxed text-ink-muted" data-id="element-311" data-mp="3mjodw1rpyzuf">Walk-ins are welcome most every day! Call ahead to ask for availability.</p>
          </div>
          <ul className="space-y-4" data-id="element-312" data-mp="3vi9s5kq5rspe">
            <li data-id="element-313" data-mp="3vh8fxcxep063">
              <a href={`tel:${contact.phoneMain}`} className="flex items-center gap-3 hover:underline" data-id="element-314" data-mp="3vgowfrs3y3py">
                <PhoneIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-315" data-mp="46q99hjzfitx" />
                {contact.phoneMain} / {contact.phoneWalkIn}
              </a>
            </li>
            <li data-id="element-316" data-mp="e44ojdu661kn">
              <a href={`sms:${contact.phoneMain}`} className="flex items-center gap-3 hover:underline" data-id="element-317" data-mp="e0gumdktbd5e">
                <MessageSquareIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-318" data-mp="i1skmk7lm0e4" />
                Text {contact.phoneMain} / {contact.phoneText}
              </a>
            </li>
            <li data-id="element-319" data-mp="1yjavbls1ppkl">
              <a href={`mailto:${contact.email}`} className="flex items-center gap-3 hover:underline" data-id="element-320" data-mp="1ymyp8m1ekdzu">
                <MailIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-321" data-mp="1tnc8i2md8fma" />
                {contact.email}
              </a>
            </li>
          </ul>
          <div className="border-t border-ink-line pt-8" data-id="element-322" data-mp="2m8vfg7zxqsvg">
            <h2 className="font-slab text-sm font-bold uppercase tracking-[0.2em]" data-id="element-323" data-mp="2hbc2dbez4m53">Save time at the shop</h2>
            <p className="mt-3 leading-relaxed text-ink-muted" data-id="element-324" data-mp="2cjtxwddqs3fd">Fill out your release form before you arrive.</p>
            <Button to="/release-form" variant="outline" className="mt-5" data-id="element-325" data-mp="27m27jnsqwpda">
              Release form
            </Button>
          </div>
        </aside>
      </section>
    </>;
}